import {
  ArrowDown,
  ArrowUpRight,
  CalendarDays,
  Clock3,
  MapPin,
  MessageCircle,
  Sparkles,
  TicketCheck,
} from "lucide-react";
import Header from "@/reusable_sections/Header";
import Footer from "@/reusable_sections/Footer";
import { homeUrl, type AcAdEvent } from "@/lib/assets";

const fallbackEvents: AcAdEvent[] = [
  {
    id: "retiro-nacional-2026",
    title: "Retiro Nacional de Pastores y Líderes 2026",
    eyebrow: "Ensanchando la Visión · Isaías 54:2",
    description:
      "Un tiempo especial de dirección, unidad, fortalecimiento ministerial y proyección para lo que Dios quiere seguir haciendo en medio de Su obra a nivel nacional.",
    notes: [
      "El viernes 19 de junio se celebrará la Asamblea General Extraordinaria.",
      "Los paquetes de alimentación y medios de pago están detallados en el formulario de registro.",
      "El hospedaje en el Campus será gratuito; debe solicitarse en el formulario y está limitado a 195 camas.",
    ],
    startDate: "2026-06-19",
    endDate: "2026-06-20",
    time: "8:30 a.m.",
    location: "Campus de las Asambleas de Dios, Moravia",
    price: "₡10.000 antes del 31 de mayo · ₡12.000 desde el 1 de junio",
    whatsapp: "8830-7750 · Solo mensajería de texto",
    registrationUrl: "https://forms.gle/PBrHFJXSBdjSeA417",
    featured: true,
  },
  {
    id: "asamblea-general-extraordinaria-2026",
    title: "Asamblea General Extraordinaria 2026",
    eyebrow: "Asamblea Nacional",
    description:
      "La Asamblea General Extraordinaria se celebrará el viernes 19 de junio, en el marco del Retiro Nacional de Pastores y Líderes 2026.",
    startDate: "2026-06-19",
    time: "Horario dentro del programa del retiro",
    location: "Campus de las Asambleas de Dios, Moravia",
    registrationUrl: "https://forms.gle/PBrHFJXSBdjSeA417",
    featured: true,
  },
];

function parseDate(value: string) {
  return new Date(`${value}T12:00:00`);
}

function dateParts(event: AcAdEvent) {
  const start = parseDate(event.startDate);
  const end = event.endDate ? parseDate(event.endDate) : null;
  const month = new Intl.DateTimeFormat("es-CR", { month: "short" }).format(start).replace(".", "");
  const year = new Intl.DateTimeFormat("es-CR", { year: "numeric" }).format(start);
  const firstDay = new Intl.DateTimeFormat("es-CR", { day: "2-digit" }).format(start);
  const lastDay = end ? new Intl.DateTimeFormat("es-CR", { day: "2-digit" }).format(end) : "";

  return {
    days: lastDay && lastDay !== firstDay ? `${firstDay}–${lastDay}` : firstDay,
    month,
    year,
  };
}

function EventCard({ event, large = false }: { event: AcAdEvent; large?: boolean }) {
  const date = dateParts(event);

  return (
    <article className={`acad-event-card ${large ? "acad-event-card--featured" : ""}`}>
      {event.image ? (
        <div className="acad-event-card-image">
          <img src={event.image} alt="" loading="lazy" decoding="async" />
        </div>
      ) : (
        <div className="acad-event-card-pattern" aria-hidden="true"><span>{date.year}</span></div>
      )}

      <div className="acad-event-card-body">
        <div className="acad-event-date">
          <strong>{date.days}</strong>
          <span>{date.month}</span>
          <small>{date.year}</small>
        </div>

        <div className="acad-event-card-copy">
          <span className="acad-events-eyebrow acad-events-eyebrow--dark">{event.eyebrow || "Evento ACAD"}</span>
          <h3>{event.title}</h3>
          <p>{event.description}</p>

          <div className="acad-event-details">
            {event.time && <span><Clock3 aria-hidden="true" />{event.time}</span>}
            {event.location && <span><MapPin aria-hidden="true" />{event.location}</span>}
            {event.price && <span><TicketCheck aria-hidden="true" />{event.price}</span>}
            {event.whatsapp && <span><MessageCircle aria-hidden="true" />{event.whatsapp}</span>}
          </div>

          {event.notes && event.notes.length > 0 && (
            <ul className="acad-event-notes">
              {event.notes.map((note) => <li key={note}>{note}</li>)}
            </ul>
          )}

          <div className="acad-event-actions">
            {event.registrationUrl && (
              <a href={event.registrationUrl} target="_blank" rel="noreferrer">
                Registro en línea <ArrowUpRight aria-hidden="true" />
              </a>
            )}
            {!event.registrationUrl && (
              <a href={homeUrl("contacto/?tema=general#contacto-formulario")}>Solicitar información <ArrowUpRight aria-hidden="true" /></a>
            )}
          </div>
        </div>
      </div>
    </article>
  );
}

export default function EventsPage() {
  const configuredEvents = window.ACAD_REACT_CONFIG?.events || [];
  const events = (configuredEvents.length ? configuredEvents : fallbackEvents)
    .slice()
    .sort((first, second) => first.startDate.localeCompare(second.startDate));
  const featuredEvents = events.filter((event) => event.featured).slice(0, 2);
  const calendarEvents = events.filter((event) => !featuredEvents.some((featured) => featured.id === event.id));
  const years = Array.from(new Set(events.map((event) => dateParts(event).year)));

  return (
    <div className="acad-react-page acad-events-page">
      <Header />

      <main>
        <section className="acad-events-hero" data-nav="dark">
          <span className="acad-events-grid" aria-hidden="true" />
          <span className="acad-events-orbit acad-events-orbit--one" aria-hidden="true" />
          <span className="acad-events-orbit acad-events-orbit--two" aria-hidden="true" />

          <div className="acad-events-shell acad-events-hero-inner">
            <div className="acad-events-hero-top">
              <span><Sparkles aria-hidden="true" /> Agenda nacional</span>
              <small>{years.join(" · ") || "2026"}</small>
            </div>

            <div className="acad-events-hero-copy">
              <span className="acad-events-eyebrow">Encuentros que nos impulsan</span>
              <h1>
                Un año para
                <span>avanzar juntos.</span>
              </h1>
              <p>
                Consulte las actividades nacionales de ACAD, conozca sus detalles y complete su
                inscripción desde un solo lugar.
              </p>
            </div>

            <a className="acad-events-scroll" href="#eventos-destacados">Ver calendario <ArrowDown aria-hidden="true" /></a>
          </div>
        </section>

        <section className="acad-events-featured" id="eventos-destacados">
          <div className="acad-events-shell">
            <div className="acad-events-heading">
              <span className="acad-events-eyebrow acad-events-eyebrow--dark">Eventos destacados</span>
              <div>
                <h2>Las grandes citas de nuestra agenda nacional.</h2>
                <p>Información oficial, fechas, lugar y acceso directo al registro.</p>
              </div>
            </div>

            <div className="acad-events-featured-grid">
              {featuredEvents.map((event) => <EventCard event={event} large key={event.id} />)}
            </div>
          </div>
        </section>

        <section className="acad-events-calendar">
          <div className="acad-events-shell">
            <div className="acad-events-calendar-top">
              <div><span className="acad-events-eyebrow acad-events-eyebrow--dark">Calendario anual</span><h2>Todos los eventos.</h2></div>
              <CalendarDays aria-hidden="true" />
            </div>

            {calendarEvents.length > 0 ? (
              <div className="acad-events-calendar-grid">
                {calendarEvents.map((event) => <EventCard event={event} key={event.id} />)}
              </div>
            ) : (
              <div className="acad-events-empty">
                <CalendarDays aria-hidden="true" />
                <div>
                  <h3>Próximamente agregaremos más fechas.</h3>
                  <p>Los nuevos eventos publicados por ACAD aparecerán automáticamente en este calendario.</p>
                </div>
              </div>
            )}
          </div>
        </section>

        <section className="acad-events-final" data-nav="dark">
          <div className="acad-events-shell acad-events-final-inner">
            <div>
              <span className="acad-events-eyebrow">Manténgase conectado</span>
              <h2>Que ninguna fecha importante le tome por sorpresa.</h2>
            </div>
            <a href={homeUrl("contacto/?tema=general#contacto-formulario")}>Consultar a ACAD <ArrowUpRight aria-hidden="true" /></a>
          </div>
        </section>
      </main>

      <Footer />
    </div>
  );
}
